import { TrailStatusFromWeatherResistanceEvent } from '../../events/TrailStatusFromWeatherResistanceEvent'
import { FreezeThawStatus } from '../../models/FreezeThawStatus.model'
import { FreezeThawStatusDTO } from '../../models/FreezeThawStatusDTO.model'
import { TrailStatus, TrailStatusInputsSnapshot } from '../../models/TrailStatus.model'
import { TrailStatusDTO, TrailStatusInputsSnapshotDTO } from '../../models/TrailStatusDTO.model'

import FlowfeedApiQueryService, {
  FlowfeedApiQueryServiceGetEvent,
} from '../FlowfeedApiQueryService/FlowfeedApiQueryService'
import { WeatherRecordService } from '../WeatherRecordService/WeatherRecordService'

export type TrailStatusSimulateEvent = {
  trailId: number
  weatherResistance?: number
  saturationModifier?: number
  dryTimeModifier?: number | null
  precipitationLookbackDays?: number | null
}

// Provenance of the cached production baseline (mirrors the API's `TrailStatusBaselineSource`).
// `cached-inputs`/`cached-derived` mean the baseline came from a persisted production status;
// `fresh-compute` means no cache was available so the baseline was computed at request time —
// in which case a same-snapshot comparison against the simulation is not guaranteed.
export type TrailStatusBaselineSource = 'cached-inputs' | 'cached-derived' | 'fresh-compute'

// Whether the simulation reused the cached baseline snapshot (`baseline-cached`, no wall-clock
// drift vs. the production baseline) or recomputed against current weather (`recomputed`).
export type TrailStatusSimulationSource = 'baseline-cached' | 'recomputed'

export type TrailStatusSimulateResult = {
  status: -1 | 0 | 1 | 2 | 3
  precipitationDecayed: number
  precipitationLastSevenDays: number
  baselineInputs?: TrailStatusInputsSnapshot
  simulationInputs?: TrailStatusInputsSnapshot
  // Provenance fields so the admin UI can tell whether the simulation is anchored to the same
  // cached production snapshot as the baseline (avoiding the time-drift confusion where a fresh
  // recompute reads e.g. 0.61 while the cached baseline read 0.65).
  source?: TrailStatusSimulationSource
  baselineSource?: TrailStatusBaselineSource
  date?: Date
  // The instant the recompute was anchored to (the cached baseline's calculation time). When the
  // simulation reuses the cached snapshot this is the authoritative "as of" time to display, since
  // `date` reflects the request instant. Falls back to `date` when the API omits it.
  referenceTime?: Date
}

export type TrailStatusBaselineResult = {
  status: -1 | 0 | 1 | 2 | 3
  precipitationDecayed: number
  precipitationLastSevenDays: number
  baselineInputs?: TrailStatusInputsSnapshot
  // Provenance of the cached production status this baseline came from, and when it was computed.
  source?: TrailStatusBaselineSource
  date?: Date
  // The cached baseline's calculation instant (the anchor the simulation recomputes against).
  // Falls back to `date` when the API omits it.
  anchorDate?: Date
}

/**
 * Error thrown by {@link TrailStatusService.simulate} that preserves the HTTP status and the
 * backend's error message so the (admin-only) UI can surface a diagnosable reason instead of a
 * generic failure. Not for public-facing surfaces.
 */
export class TrailStatusSimulateError extends Error {
  public readonly httpStatus?: number

  constructor(message: string, httpStatus?: number) {
    super(message)
    this.name = 'TrailStatusSimulateError'
    this.httpStatus = httpStatus
  }
}

/**
 * Pull an HTTP status and a human-readable message out of whatever the API layer threw. The
 * `FlowfeedApiQueryService` rethrows the axios `error.response` (so `{ status, data }`) on HTTP
 * errors, or the raw axios error when there was no response (network/timeout).
 */
const extractSimulateError = (error: unknown): TrailStatusSimulateError => {
  const fallbackMessage = 'Sorry, there was a problem running the simulation.'
  if (error && typeof error === 'object') {
    const candidate = error as {
      status?: number
      data?: unknown
      message?: string
    }
    const httpStatus = typeof candidate.status === 'number' ? candidate.status : undefined

    let backendMessage: string | undefined
    const data = candidate.data
    if (typeof data === 'string') {
      backendMessage = data
    } else if (data && typeof data === 'object') {
      const body = data as { message?: unknown; error?: unknown }
      if (typeof body.message === 'string') {
        backendMessage = body.message
      } else if (typeof body.error === 'string') {
        backendMessage = body.error
      }
    }
    if (!backendMessage && typeof candidate.message === 'string') {
      backendMessage = candidate.message
    }

    if (httpStatus !== undefined || backendMessage) {
      return new TrailStatusSimulateError(backendMessage ?? fallbackMessage, httpStatus)
    }
  }
  return new TrailStatusSimulateError(fallbackMessage)
}

type TrailStatusSimulateResultDTO = {
  date?: string
  referenceTime?: string
  trailId?: number
  status: -1 | 0 | 1 | 2 | 3
  source?: TrailStatusSimulationSource
  baselineSource?: TrailStatusBaselineSource
  baselineInputs?: TrailStatusInputsSnapshotDTO
  simulationInputs?: TrailStatusInputsSnapshotDTO
  overridesApplied?: TrailStatusInputsSnapshotDTO
}

type TrailStatusBaselineResultDTO = {
  date?: string
  anchorDate?: string
  trailId?: number
  status: -1 | 0 | 1 | 2 | 3
  source?: TrailStatusBaselineSource
  // The cached baseline endpoint may return its inputs under either key; accept both so the UI
  // does not break if the backend ships one or the other.
  baselineInputs?: TrailStatusInputsSnapshotDTO
  inputs?: TrailStatusInputsSnapshotDTO
}

// The API returns a string date; coerce to a Date only when present and valid.
const parseResultDate = (raw?: string): Date | undefined => {
  if (!raw) {
    return undefined
  }
  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

export class TrailStatusService {
  /**
   * @description Fetch a trail status from the supplied weather resistance and weather data
   *
   * @param weatherResistance 1-20
   * @param precipDecayed amount of rain still on the trail
   * @param precipLastSevenDays amount of rain in the lookback window (legacy name)
   * @param saturationModifier 0-1 (eg. 0.5 = 50%)
   *
   * @returns promise for a a status of 1, 2, or 3
   */
  public static async statusFromWeatherResistance({
    weatherResistance,
    precipDecayed,
    precipLastSevenDays,
    saturationModifier,
  }: TrailStatusFromWeatherResistanceEvent): Promise<1 | 2 | 3> {
    const params = {
      weatherResistance,
      precipDecayed,
      precipLastSevenDays,
      saturationModifier,
    }
    const flowfeedApiQueryRequest: FlowfeedApiQueryServiceGetEvent = {
      endpoint: `trail-status/status-from-weather-resistance`,
      params,
    }

    const flowfeedApiQueryService = new FlowfeedApiQueryService()
    try {
      return flowfeedApiQueryService.get<1 | 2 | 3>(flowfeedApiQueryRequest)
    } catch (error) {
      console.error(error)
      throw 'Sorry, there was a problem with your request.'
    }
  }

  /**
   * @description Run the admin current-conditions simulation for a trail. The backend recomputes
   * precipitation totals/decayed precipitation from current recent weather history using the
   * supplied overrides (resistance, saturation modifier, dry-time modifier, lookback days) and
   * returns the resulting status along with the inputs/precipitation values it actually used.
   *
   * The endpoint path is isolated here so it can be adjusted in one place if the backend ships
   * under a different path.
   */
  public static async simulate(event: TrailStatusSimulateEvent): Promise<TrailStatusSimulateResult> {
    const flowfeedApiQueryService = new FlowfeedApiQueryService()
    try {
      const data = await flowfeedApiQueryService.post<TrailStatusSimulateResultDTO>({
        endpoint: 'trail-status/simulate',
        data: event,
      })
      return {
        status: data.status,
        precipitationDecayed: data.simulationInputs?.precipitationDecayed ?? 0,
        precipitationLastSevenDays: data.simulationInputs?.precipitationLastSevenDays ?? 0,
        baselineInputs: data.baselineInputs,
        simulationInputs: data.simulationInputs,
        source: data.source,
        baselineSource: data.baselineSource,
        date: parseResultDate(data.date),
        referenceTime: parseResultDate(data.referenceTime) ?? parseResultDate(data.date),
      }
    } catch (error) {
      console.error(error)
      throw extractSimulateError(error)
    }
  }

  /**
   * @description Fetch the cached production baseline for a trail. Unlike {@link simulate}, this
   * reads the most recent persisted production status calculation (status + the inputs/precipitation
   * values it used) rather than recomputing from current weather. Because it returns the cached
   * live status, the "Production" card and override defaults still populate for normal live trails
   * even when a fresh simulate would fail — so the fallback banner should not show in that case.
   *
   * The endpoint path is isolated here so it can be adjusted in one place if the backend ships
   * the cached baseline under a different path. Accepts inputs under `baselineInputs` or `inputs`.
   */
  public static async getBaseline(trailId: number): Promise<TrailStatusBaselineResult> {
    const flowfeedApiQueryService = new FlowfeedApiQueryService()
    try {
      const data = await flowfeedApiQueryService.get<TrailStatusBaselineResultDTO>({
        endpoint: `trail-status/baseline/${trailId}`,
      })
      const baselineInputs = data.baselineInputs ?? data.inputs
      return {
        status: data.status,
        precipitationDecayed: baselineInputs?.precipitationDecayed ?? 0,
        precipitationLastSevenDays: baselineInputs?.precipitationLastSevenDays ?? 0,
        baselineInputs,
        source: data.source,
        date: parseResultDate(data.date),
        anchorDate: parseResultDate(data.anchorDate) ?? parseResultDate(data.date),
      }
    } catch (error) {
      console.error(error)
      throw extractSimulateError(error)
    }
  }

  public static formatTrailStatus(trailStatus: TrailStatusDTO): TrailStatus {
    return {
      date: new Date(trailStatus.date),
      status: trailStatus.status,
      displayWeather: trailStatus.displayWeather
        ? WeatherRecordService.formatWeatherRecord(trailStatus.displayWeather)
        : undefined,
      precipitationDecayed: trailStatus.precipitationDecayed,
      precipitationLastTwoHours: trailStatus.precipitationLastTwoHours,
      precipitationLastFortyEightHours: trailStatus.precipitationLastFortyEightHours,
      precipitationLastSevenDays: trailStatus.precipitationLastSevenDays,
      freezeThawStatus: TrailStatusService.formatFreezeThawStatus(trailStatus.freezeThawStatus),
      inputs: trailStatus.inputs,
      inputsSnapshot: trailStatus.inputsSnapshot,
    }
  }

  public static formatFreezeThawStatus(freezeThawStatus: FreezeThawStatusDTO): FreezeThawStatus {
    return {
      date: new Date(freezeThawStatus.date),
      freezeCounter: freezeThawStatus.freezeCounter,
      thawCounter: freezeThawStatus.thawCounter,
      status: freezeThawStatus.status,
    }
  }
}
